import React, { useEffect, useRef, useState } from 'react';
import { Megaphone, Edit3, Trash2, X, Save } from 'lucide-react';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
  fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });

const PERIODS = Array.from({ length: 12 }, (_, i) => i + 1);

interface ProductOption {
  _id: string;
  title: string;
  active: boolean;
}

interface OfferOption {
  _id: string;
  sourceProductId: { title: string };
  recommendedProductId: { title: string };
  active: boolean;
}

interface Campaign {
  _id: string;
  title: string;
  message: string;
  targetPeriod: number | null;
  targetMode: 'all' | 'has_product' | 'lacks_product';
  targetType: 'product' | 'offer';
  targetId: string | null;
  targetTitle: string | null;
  ctaLabel: string;
  active: boolean;
  createdAt: string;
}

interface FormState {
  title: string;
  message: string;
  targetPeriod: string;
  targetMode: Campaign['targetMode'];
  targetType: Campaign['targetType'];
  targetId: string;
  ctaLabel: string;
  active: boolean;
}

const emptyForm: FormState = {
  title: '', message: '', targetPeriod: '', targetMode: 'all',
  targetType: 'product', targetId: '', ctaLabel: 'Ver oferta', active: true,
};

const TARGET_MODE_LABELS: Record<Campaign['targetMode'], string> = {
  all: 'Todos os alunos',
  has_product: 'Quem já tem',
  lacks_product: 'Quem NÃO tem',
};

interface RyanAdminCampaignsProps {
  products: ProductOption[];
  // Controle externo (aba Marketing de RyanAdminProducts, botões "Criar
  // campanha"/"Ver campanhas") da alternância lista/formulário — mesmo
  // padrão de activeTab/onTabChange já usado em RyanAdminProducts pro
  // controle externo do RyanAdminHub. Sem os 3 props, cai pro estado
  // interno de sempre (nenhum outro lugar usa este componente standalone
  // hoje, mas o fallback evita acoplar o componente a ter sempre um pai
  // controlador).
  mode?: 'list' | 'form';
  onModeChange?: (mode: 'list' | 'form') => void;
  // Incrementa a cada clique externo em "Criar campanha" — dispara reset
  // pro formulário em branco. Necessário porque só mudar `mode` de 'list'
  // pra 'form' não distingue "abrir em branco" (botão externo) de "abrir
  // pra editar" (openEdit já preenche form/editingId antes da transição).
  createSignal?: number;
}

const RyanAdminCampaigns: React.FC<RyanAdminCampaignsProps> = ({ products, mode: externalMode, onModeChange, createSignal }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [offers, setOffers] = useState<OfferOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [internalShowForm, setInternalShowForm] = useState(false);
  const isExternallyControlled = externalMode !== undefined;
  const showForm = isExternallyControlled ? externalMode === 'form' : internalShowForm;
  const setShowForm = (v: boolean) => {
    if (isExternallyControlled) onModeChange?.(v ? 'form' : 'list');
    else setInternalShowForm(v);
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [campaignsRes, offersRes] = await Promise.all([
        api('/admin/campanhas'),
        api('/admin/upsell-offers'),
      ]);
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      if (offersRes.ok) setOffers(await offersRes.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setShowForm(true);
  };

  // Dispara openCreate() a cada incremento de createSignal (clique externo
  // em "Criar campanha") — pula o valor inicial (montagem) pra não abrir o
  // formulário sozinho ao entrar no modo "ver campanhas" pela primeira vez.
  const prevCreateSignal = useRef(createSignal);
  useEffect(() => {
    if (createSignal === undefined) return;
    if (prevCreateSignal.current === undefined) { prevCreateSignal.current = createSignal; return; }
    if (createSignal !== prevCreateSignal.current) {
      prevCreateSignal.current = createSignal;
      openCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSignal]);

  const openEdit = (c: Campaign) => {
    setEditingId(c._id);
    setForm({
      title: c.title,
      message: c.message || '',
      targetPeriod: c.targetPeriod === null ? '' : String(c.targetPeriod),
      targetMode: c.targetMode,
      targetType: c.targetType,
      targetId: c.targetId || '',
      ctaLabel: c.ctaLabel || 'Ver oferta',
      active: c.active,
    });
    setError(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Título é obrigatório.'); return; }
    if (form.targetMode !== 'all' && !form.targetId) {
      setError('Selecione um produto ou oferta de referência para este modo de segmentação.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        title: form.title,
        message: form.message,
        targetPeriod: form.targetPeriod === '' ? null : Number(form.targetPeriod),
        targetMode: form.targetMode,
        targetType: form.targetType,
        targetId: form.targetMode === 'all' ? null : form.targetId,
        ctaLabel: form.ctaLabel,
        active: form.active,
      });
      const res = editingId
        ? await api(`/admin/campanhas/${editingId}`, { method: 'PUT', body })
        : await api('/admin/campanhas', { method: 'POST', body });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Erro ao salvar campanha.');
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro de conexão.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    await api(`/admin/campanhas/${id}`, { method: 'DELETE' });
    setConfirmDeleteId(null);
    await load();
  };

  return (
    <div>
      <div className="rap-upsell-header">
        <div>
          <h3 className="rap-upsell-title"><Megaphone size={16} /> {showForm ? (editingId ? 'Editar campanha' : 'Nova campanha') : 'Campanhas'}</h3>
          <p className="rap-hint">
            Mensagens segmentadas mostradas dentro da plataforma (RyanHomePage) pros alunos elegíveis.
          </p>
        </div>
      </div>

      {!showForm && (
        <div className="rap-upsell-list">
          {loading && <p className="rap-hint">Carregando…</p>}
          {!loading && campaigns.length === 0 && (
            <div className="rap-empty">
              <Megaphone size={32} color="#0D3B0E" style={{ opacity: 0.2 }} />
              <p>Nenhuma campanha criada ainda.</p>
            </div>
          )}
          {campaigns.map(c => (
            <div key={c._id} className="rap-upsell-offer-card">
              <div className="rap-upsell-offer-info">
                <div className="rap-upsell-offer-title">
                  {c.title}
                  {!c.active && <span className="rap-upsell-inactive-badge">Inativa</span>}
                </div>
                <div className="rap-upsell-offer-members">
                  {TARGET_MODE_LABELS[c.targetMode]}{c.targetTitle ? ` "${c.targetTitle}"` : ''}
                  {c.targetPeriod !== null ? ` · ${c.targetPeriod}° período` : ' · todos os períodos'}
                </div>
              </div>
              <div className="rap-upsell-offer-actions">
                <button type="button" className="rap-btn-icon" onClick={() => openEdit(c)} title="Editar">
                  <Edit3 size={14} />
                </button>
                {confirmDeleteId === c._id ? (
                  <>
                    <button type="button" className="rap-confirm-yes" onClick={() => handleDelete(c._id)}>Sim</button>
                    <button type="button" className="rap-btn-icon" onClick={() => setConfirmDeleteId(null)}><X size={13} /></button>
                  </>
                ) : (
                  <button type="button" className="rap-btn-icon rap-btn-icon--danger" onClick={() => setConfirmDeleteId(c._id)} title="Excluir">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="rap-upsell-form">
          <div className="rap-field">
            <label className="rap-label">Título</label>
            <input className="rap-input" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Ex: Aproveite o combo de anatomia" />
          </div>

          <div className="rap-field">
            <label className="rap-label">Mensagem</label>
            <textarea className="rap-textarea" rows={3} value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="Texto exibido no banner/modal da campanha..." />
          </div>

          <div className="rap-field-row">
            <div className="rap-field">
              <label className="rap-label">Período</label>
              <select className="rap-select" value={form.targetPeriod}
                onChange={e => setForm(f => ({ ...f, targetPeriod: e.target.value }))}>
                <option value="">Todos os períodos</option>
                {PERIODS.map(p => <option key={p} value={p}>{p}° período</option>)}
              </select>
            </div>
            <div className="rap-field">
              <label className="rap-label">Segmentação</label>
              <select className="rap-select" value={form.targetMode}
                onChange={e => setForm(f => ({ ...f, targetMode: e.target.value as Campaign['targetMode'] }))}>
                <option value="all">Todos os alunos</option>
                <option value="has_product">Quem já tem um produto/oferta</option>
                <option value="lacks_product">Quem NÃO tem um produto/oferta</option>
              </select>
            </div>
          </div>

          {form.targetMode !== 'all' && (
            <div className="rap-field-row">
              <div className="rap-field">
                <label className="rap-label">Tipo de alvo</label>
                <select className="rap-select" value={form.targetType}
                  onChange={e => setForm(f => ({ ...f, targetType: e.target.value as Campaign['targetType'], targetId: '' }))}>
                  <option value="product">Produto avulso</option>
                  <option value="offer">Recomendação de produto</option>
                </select>
              </div>
              <div className="rap-field">
                <label className="rap-label">{form.targetType === 'product' ? 'Produto' : 'Recomendação'} de referência</label>
                <select className="rap-select" value={form.targetId}
                  onChange={e => setForm(f => ({ ...f, targetId: e.target.value }))}>
                  <option value="">— selecione —</option>
                  {form.targetType === 'product'
                    ? products.filter(p => p.active).map(p => <option key={p._id} value={p._id}>{p.title}</option>)
                    : offers.filter(o => o.active).map(o => (
                        <option key={o._id} value={o._id}>
                          {o.sourceProductId?.title} → {o.recommendedProductId?.title}
                        </option>
                      ))}
                </select>
              </div>
            </div>
          )}

          <div className="rap-field">
            <label className="rap-label">Texto do botão (CTA)</label>
            <input className="rap-input" value={form.ctaLabel}
              onChange={e => setForm(f => ({ ...f, ctaLabel: e.target.value }))}
              placeholder="Ver oferta" />
          </div>

          <label className="rap-check">
            <input type="checkbox" checked={form.active}
              onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
            Ativa
          </label>

          {error && <p className="rap-upsell-error">{error}</p>}

          <div className="rap-upsell-form-actions">
            <button type="button" className="rap-btn-primary" onClick={handleSave} disabled={saving}>
              <Save size={14} /> {saving ? 'Salvando…' : 'Salvar campanha'}
            </button>
            <button type="button" className="rap-btn-ghost" onClick={() => setShowForm(false)} disabled={saving}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RyanAdminCampaigns;
